import {Subject} from "rxjs";
import {FromNativeAppEventTypes} from "./FromNativeAppEventTypes";
import {logger} from "../../log/logger";
import {INativeRequestResponseContext} from "../../nativeBridge";
import {filter} from "rxjs/operators";
import {ILogArguments} from "../../log/ILogArguments";

const messageFromNativeAppSubject = new Subject<FromNativeAppEventTypes | FromNativeAppEventTypes & INativeRequestResponseContext>();

export function subscribeToReceiveMessagesFromNativeApp(callback: (message: FromNativeAppEventTypes) => void){
  const protectedCallback = createProtectedCallback(callback);
  const subscription = messageFromNativeAppSubject.subscribe(protectedCallback);
  return subscription.unsubscribe.bind(subscription);
}

// Type guard to detect "response" shape (which holds the request's context) vs. "receive message" shape
function isResponseMessage(
  msg: FromNativeAppEventTypes | (FromNativeAppEventTypes & INativeRequestResponseContext)
): msg is FromNativeAppEventTypes & INativeRequestResponseContext {
  return "context" in msg;
}

/** This should only be used in the nativeBridge class */
export function subscribeToResponsesFromNativeApp(callback: (message2: FromNativeAppEventTypes & INativeRequestResponseContext) => void){
  const protectedCallback = createProtectedCallbackForResponses(callback);
  const subscription = messageFromNativeAppSubject.pipe(filter(isResponseMessage)).subscribe(protectedCallback);
  return subscription.unsubscribe.bind(subscription);
}

function createProtectedCallback(callback: (message: FromNativeAppEventTypes | FromNativeAppEventTypes & INativeRequestResponseContext) => void){
  return (message: FromNativeAppEventTypes | FromNativeAppEventTypes & INativeRequestResponseContext) => {
    try {
      callback(message);
    } catch(e) {
      logger.nativeToWebviewMessage.error({
        message: `Native --> webview: error encountered in observer of receiving native app messages`,
        // The safest approach is to check whether e is actually an Error (using instanceof) and handle it accordingly.
        //   This way, if e is already an Error, you’ll pass it along.
        //   Otherwise, you wrap it in a new Error object so that your logger always gets a valid Error type.
        error: e instanceof Error ? e : new Error(String(e)),
      });
    }
  };
}

function createProtectedCallbackForResponses(callback: (message: FromNativeAppEventTypes & INativeRequestResponseContext) => void){
  return (message: FromNativeAppEventTypes & INativeRequestResponseContext) => {
    try {
      callback(message);
    } catch(e) {
      logger.nativeToWebviewMessage.error({
        message: `Native --> webview: error encountered in observer of receiving native app messages`,
        error: e as Error
      });
    }
  };
}

export function publishMessageReceivedFromNativeApp(message: FromNativeAppEventTypes){
  messageFromNativeAppSubject.next(message);
}
